import {
  type APIMessage,
  formatDayDivider,
  formatMessageTime,
  formatShortTime,
  mentionsMe,
  messageAbilities,
  MessageType,
  type PendingMessage,
  parseMarkdown,
  selectDisplayName,
  systemMessageText,
  t,
  toPlainText,
} from '@alternativamais/chat-core';
import { memo, useMemo } from 'react';
import { useNavigate } from 'react-router';

import { Avatar } from '../../../componentes/Avatar';
import { avisarErro } from '../../../componentes/Avisos';
import { Icone, type NomeIcone } from '../../../componentes/Icone';
import { Markdown } from '../../../componentes/Markdown';
import { abrirCamada } from '../../../chat/camadas';
import { useCliente, useEstado, useEstadoRaso } from '../../../chat/contexto';
import {
  useAjusteDaConta,
  useCorDoNome,
  useEu,
  useNome,
} from '../../../chat/ganchos';
import { usePreferencias } from '../../../chat/preferencias';
import { Enquete, ResultadoEnquete } from '../enquete/Enquete';
import { Acoes } from './Acoes';
import { Anexos, Embeds, Encaminhada, Figurinhas } from './Conteudo';
import { useCanal } from './contexto';
import { Edicao } from './Edicao';
import { Reacoes } from './Reacoes';

const HORA_COMPLETA = new Intl.DateTimeFormat('pt-BR', {
  dateStyle: 'full',
  timeStyle: 'short',
});

function Hora({ quando, curta = false }: { quando: string; curta?: boolean }) {
  return (
    <time
      dateTime={quando}
      title={HORA_COMPLETA.format(new Date(quando))}
      className="text-xs whitespace-nowrap text-texto-3"
    >
      {curta ? formatShortTime(quando) : formatMessageTime(quando)}
    </time>
  );
}

/** Abre o perfil (C12) ao lado do que foi clicado. */
export function abrirPerfil(
  evento: React.MouseEvent<HTMLElement>,
  userId: string,
  guildId: string | null,
) {
  const caixa = evento.currentTarget.getBoundingClientRect();
  abrirCamada({
    tipo: 'perfil',
    userId,
    guildId,
    ancora: { x: caixa.right + 8, y: caixa.top },
  });
}

export function NomeDoAutor({
  autor,
  guildId,
  clicavel = false,
}: {
  autor: APIMessage['author'];
  guildId: string | null;
  /** Clicar abre o perfil (na lista de mensagens). */
  clicavel?: boolean;
}) {
  const nome = useNome(autor.id, guildId, autor);
  const corDoCargo = useCorDoNome(autor.id, guildId);
  // Cores de cargo (U8): no nome, numa bolinha ao lado ou sem cor.
  const coresDeCargo = usePreferencias((prefs) => prefs.coresDeCargo);
  const cor = coresDeCargo === 'nome' ? corDoCargo : null;
  return (
    <span className="flex min-w-0 items-center gap-1.5">
      {coresDeCargo === 'lado' && corDoCargo && (
        <span
          aria-hidden="true"
          className="size-2.5 shrink-0 rounded-full"
          style={{ background: corDoCargo }}
        />
      )}
      {clicavel ? (
        <button
          type="button"
          onClick={(evento) => abrirPerfil(evento, autor.id, guildId)}
          className="truncate text-left text-[15px] font-semibold hover:underline"
          style={cor ? { color: cor } : undefined}
        >
          {nome}
        </button>
      ) : (
        <span
          className="truncate text-[15px] font-semibold"
          style={cor ? { color: cor } : undefined}
        >
          {nome}
        </span>
      )}
      {autor.bot && (
        <span className="shrink-0 rounded bg-destaque-suave px-[5px] py-px text-[10px] font-bold text-destaque-claro">
          BOT
        </span>
      )}
    </span>
  );
}

/** A mensagem citada, numa linha, acima do nome (resposta). */
function RespostaCitada({
  mensagem,
  guildId,
}: {
  mensagem: APIMessage;
  guildId: string | null;
}) {
  const canal = useCanal();
  const citada = mensagem.referenced_message;
  const nomes = useEstado((estado) => estado.users);
  const cargos = useEstado((estado) =>
    guildId ? estado.guilds[guildId]?.roles : undefined,
  );
  const canais = useEstado((estado) => estado.channels);
  const previa = useMemo(() => {
    if (!citada?.content) return null;
    return toPlainText(parseMarkdown(citada.content, { inline: true }), {
      user: (id) =>
        nomes[id]?.global_name ?? nomes[id]?.username ?? 'desconhecido',
      role: (id) => cargos?.[id]?.name ?? 'cargo-desconhecido',
      channel: (id) => canais[id]?.name ?? 'canal-desconhecido',
    });
  }, [citada, nomes, cargos, canais]);
  return (
    <div className="relative mb-1 flex min-w-0 items-center gap-1.5 pl-[56px] text-[13px] text-texto-3">
      <span
        aria-hidden="true"
        className="absolute top-[9px] left-[36px] h-3 w-[18px] rounded-tl-md border-t-2 border-l-2 border-ativo"
      />
      {citada ? (
        <>
          <Avatar
            pessoa={citada.author}
            nome={citada.author.username}
            tamanho={16}
          />
          <span className="shrink-0 font-semibold text-texto-2">
            @
            <NomeSimples
              id={citada.author.id}
              guildId={guildId}
              reserva={citada.author}
            />
          </span>
          <button
            type="button"
            onClick={() => canal.irPara(citada.id)}
            className="min-w-0 truncate text-left hover:text-texto"
          >
            {previa ??
              (citada.attachments.length || citada.embeds.length ? (
                <span className="flex items-center gap-1 italic">
                  Clique para ver o anexo <Icone nome="imagem" tamanho={14} />
                </span>
              ) : (
                <span className="italic">mensagem sem texto</span>
              ))}
          </button>
        </>
      ) : (
        <span className="italic">{t('mensagem.apagada')}</span>
      )}
    </div>
  );
}

function NomeSimples({
  id,
  guildId,
  reserva,
}: {
  id: string;
  guildId: string | null;
  reserva: APIMessage['author'];
}) {
  return <>{useNome(id, guildId, reserva)}</>;
}

/** Mensagem do sistema (entrou, fixou, thread…): ícone e frase. */
const ICONES_DO_SISTEMA: Partial<Record<number, NomeIcone>> = {
  [MessageType.RECIPIENT_ADD]: 'usuario-mais',
  [MessageType.RECIPIENT_REMOVE]: 'sair',
  [MessageType.CHANNEL_NAME_CHANGE]: 'editar',
  [MessageType.CHANNEL_ICON_CHANGE]: 'editar',
  [MessageType.CHANNEL_PINNED_MESSAGE]: 'pin',
  [MessageType.USER_JOIN]: 'usuario-mais',
  [MessageType.THREAD_CREATED]: 'thread',
  [MessageType.AUTO_MODERATION_ACTION]: 'escudo',
  [MessageType.POLL_RESULT]: 'enquete',
};

function LinhaSistema({
  mensagem,
  texto,
  children,
}: {
  mensagem: APIMessage;
  texto?: string;
  children?: React.ReactNode;
}) {
  return (
    <div
      data-mensagem={mensagem.id}
      className="group relative flex items-start gap-4 px-4 pt-3 pb-0.5 hover:bg-white/[.03]"
    >
      <span className="flex w-10 shrink-0 justify-center pt-0.5 text-texto-3">
        <Icone nome={ICONES_DO_SISTEMA[mensagem.type] ?? 'info'} tamanho={18} />
      </span>
      {children ?? (
        <p className="m-0 min-w-0 text-[15px] leading-[1.45] text-texto-2">
          {texto} <Hora quando={mensagem.timestamp} />
        </p>
      )}
    </div>
  );
}

/** "Fulano iniciou uma thread: Nome" (tipo 18): o nome abre a thread. */
function ThreadCriada({
  mensagem,
  guildId,
}: {
  mensagem: APIMessage;
  guildId: string | null;
}) {
  const navegar = useNavigate();
  const autor = useNome(mensagem.author.id, guildId, mensagem.author);
  const threadId = mensagem.message_reference?.channel_id;
  const thread = useEstado((estado) =>
    threadId ? estado.channels[threadId] : undefined,
  );
  return (
    <LinhaSistema mensagem={mensagem}>
      <p className="m-0 min-w-0 text-[15px] leading-[1.45] text-texto-2">
        <strong className="font-semibold text-texto">{autor}</strong> iniciou
        uma thread:{' '}
        {threadId ? (
          <button
            type="button"
            onClick={() =>
              navegar(
                `/canais/${guildId ?? '@me'}/${mensagem.channel_id}/threads/${threadId}`,
              )
            }
            className="font-semibold text-texto hover:underline"
          >
            {thread?.name ?? mensagem.content}
          </button>
        ) : (
          <strong className="font-semibold text-texto">
            {mensagem.content}
          </strong>
        )}
        . <Hora quando={mensagem.timestamp} />
      </p>
    </LinhaSistema>
  );
}

/** Começo da thread (tipo 21): a mensagem de onde ela saiu (C2). */
function InicioDaThread({
  mensagem,
  guildId,
}: {
  mensagem: APIMessage;
  guildId: string | null;
}) {
  const navegar = useNavigate();
  const original = mensagem.referenced_message;
  const pai = mensagem.message_reference?.channel_id;
  return (
    <div data-mensagem={mensagem.id} className="px-4 pt-3 pb-1">
      <span className="text-xs font-semibold tracking-[.04em] text-texto-3 uppercase">
        Iniciada a partir de
      </span>
      {original ? (
        <div className="mt-1.5 flex gap-3 rounded-lg border border-borda bg-elevado p-3">
          <Avatar
            pessoa={original.author}
            nome={original.author.global_name ?? original.author.username}
            tamanho={32}
            fundo="var(--cor-elevado)"
          />
          <div className="flex min-w-0 grow flex-col gap-0.5">
            <div className="flex min-w-0 items-baseline gap-2">
              <NomeDoAutor autor={original.author} guildId={guildId} clicavel />
              <Hora quando={original.timestamp} />
            </div>
            {original.content && (
              <div className="text-[15px] leading-[1.45]">
                <Markdown texto={original.content} guildId={guildId} />
              </div>
            )}
            <Anexos anexos={original.attachments} mensagem={original} />
            {pai && (
              <button
                type="button"
                onClick={() =>
                  navegar(`/canais/${guildId ?? '@me'}/${pai}/${original.id}`)
                }
                className="mt-1 w-fit text-[13px] font-medium text-link hover:underline"
              >
                Ir para a mensagem
              </button>
            )}
          </div>
        </div>
      ) : (
        <p className="mt-1 mb-0 text-sm text-texto-3 italic">
          {t('mensagem.apagada')}
        </p>
      )}
    </div>
  );
}

/** Uma mensagem da lista (com avatar e nome, ou continuação agrupada). */
export const Mensagem = memo(function Mensagem({
  mensagem,
  agrupada,
}: {
  mensagem: APIMessage;
  agrupada: boolean;
}) {
  const canal = useCanal();
  const cliente = useCliente();
  const guildId = canal.guildId;
  const mencionaMe = useEstado((estado) => mentionsMe(estado, mensagem));
  const pode = useEstadoRaso((estado) =>
    estado.me ? messageAbilities(estado, mensagem) : null,
  );
  const nomeDe = useEstado((estado) => estado.users);
  const compacto = useAjusteDaConta('message_display_compact');
  const mostrarReacoes = useAjusteDaConta('render_reactions');
  const mostrarPrevias = useAjusteDaConta('render_embeds');
  const avataresNoCompacto = usePreferencias(
    (prefs) => prefs.avataresNoCompacto,
  );
  const sistema = useMemo(
    () =>
      systemMessageText(mensagem, (id) =>
        selectDisplayName(cliente.store.getState(), id, guildId),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mensagem, guildId, nomeDe],
  );
  if (mensagem.type === MessageType.THREAD_STARTER_MESSAGE) {
    return <InicioDaThread mensagem={mensagem} guildId={guildId} />;
  }
  if (mensagem.type === MessageType.THREAD_CREATED) {
    return <ThreadCriada mensagem={mensagem} guildId={guildId} />;
  }
  if (mensagem.type === MessageType.POLL_RESULT) {
    const enqueteId = mensagem.message_reference?.message_id;
    return (
      <LinhaSistema mensagem={mensagem}>
        <ResultadoEnquete
          mensagem={mensagem}
          autor={selectDisplayName(
            cliente.store.getState(),
            mensagem.author.id,
            guildId,
          )}
          aoVer={enqueteId ? () => canal.irPara(enqueteId) : undefined}
        />
      </LinhaSistema>
    );
  }
  if (sistema !== null) {
    return <LinhaSistema mensagem={mensagem} texto={sistema} />;
  }
  const editando = canal.editandoId === mensagem.id;
  // Só o link de um gif/imagem: o Discord mostra só a mídia.
  const soLinkDeMidia =
    !!mensagem.content &&
    mensagem.embeds.length === 1 &&
    (mensagem.embeds[0].type === 'gifv' ||
      mensagem.embeds[0].type === 'image') &&
    mensagem.content.trim() === mensagem.embeds[0].url;
  const destacada = canal.destacadaId === mensagem.id;
  const resposta =
    mensagem.type === MessageType.REPLY && !!mensagem.message_reference;
  const encaminhada = mensagem.message_reference?.type === 1;
  const conteudo = editando ? (
    <Edicao mensagem={mensagem} />
  ) : (
    mensagem.content &&
    !soLinkDeMidia && (
      <div
        className={`${compacto ? 'inline' : ''} text-[length:var(--fonte-chat)] leading-[1.45]`}
      >
        <Markdown
          texto={mensagem.content}
          guildId={guildId}
          className="inline"
        />
        {mensagem.edited_timestamp && (
          <span
            className="ml-1 text-xs text-texto-3"
            title={HORA_COMPLETA.format(new Date(mensagem.edited_timestamp))}
          >
            {t('mensagem.editada')}
          </span>
        )}
      </div>
    )
  );
  const resto = (
    <>
      {encaminhada && <Encaminhada mensagem={mensagem} guildId={guildId} />}
      <Anexos anexos={mensagem.attachments} mensagem={mensagem} />
      {mostrarPrevias && <Embeds embeds={mensagem.embeds} guildId={guildId} />}
      {mensagem.sticker_items && <Figurinhas itens={mensagem.sticker_items} />}
      {mensagem.poll && <Enquete mensagem={mensagem} />}
      {mensagem.thread && <ChipThread mensagem={mensagem} />}
      {mostrarReacoes && (
        <Reacoes mensagem={mensagem} podeReagir={!!pode?.react} />
      )}
    </>
  );
  return (
    <article
      data-mensagem={mensagem.id}
      aria-label={mencionaMe ? 'Menciona você' : undefined}
      className={`group relative flex flex-col pr-4 pl-4 ${
        compacto
          ? agrupada
            ? 'py-px'
            : 'pt-[calc(var(--espaco-grupos)/3)] pb-px'
          : agrupada
            ? 'py-0.5'
            : 'pt-[var(--espaco-grupos)] pb-0.5'
      } ${
        destacada
          ? 'bg-destaque-suave'
          : mencionaMe
            ? 'bg-[rgba(245,154,69,.08)] hover:bg-[rgba(245,154,69,.11)]'
            : editando
              ? 'bg-editando'
              : 'hover:bg-white/[.03]'
      }`}
    >
      {(mencionaMe || destacada) && (
        <span
          aria-hidden="true"
          className="absolute inset-y-0 left-0 w-0.5 bg-destaque"
        />
      )}
      {resposta && <RespostaCitada mensagem={mensagem} guildId={guildId} />}
      {compacto ? (
        // Compacto (C53): hora, nome e texto na mesma linha.
        <div className="flex gap-2">
          <span className="w-[52px] shrink-0 pt-[3px] text-right text-[11px] leading-[1.45]">
            <Hora quando={mensagem.timestamp} curta />
          </span>
          <div className="min-w-0 grow">
            <span className="mr-1.5 inline-flex items-baseline gap-1.5 align-baseline">
              {avataresNoCompacto && (
                <span className="self-center">
                  <Avatar
                    pessoa={mensagem.author}
                    nome={
                      mensagem.author.global_name ?? mensagem.author.username
                    }
                    tamanho={18}
                  />
                </span>
              )}
              <NomeDoAutor
                autor={mensagem.author}
                guildId={guildId}
                clicavel={!mensagem.webhook_id}
              />
            </span>
            {conteudo}
            {resto}
          </div>
        </div>
      ) : (
        <div className="flex gap-4">
          {agrupada ? (
            <span className="w-10 shrink-0 pt-[3px] text-right text-[11px] leading-[1.45] opacity-0 group-hover:opacity-100">
              <Hora quando={mensagem.timestamp} curta />
            </span>
          ) : (
            <button
              type="button"
              aria-label={`Perfil de ${mensagem.author.global_name ?? mensagem.author.username}`}
              disabled={!!mensagem.webhook_id}
              onClick={(evento) =>
                abrirPerfil(evento, mensagem.author.id, guildId)
              }
              className="h-10 shrink-0 self-start rounded-full disabled:cursor-default"
            >
              <Avatar
                pessoa={mensagem.author}
                nome={mensagem.author.global_name ?? mensagem.author.username}
              />
            </button>
          )}
          <div className="flex min-w-0 grow flex-col">
            {!agrupada && (
              <div className="flex min-w-0 items-baseline gap-2">
                <NomeDoAutor
                  autor={mensagem.author}
                  guildId={guildId}
                  clicavel={!mensagem.webhook_id}
                />
                <Hora quando={mensagem.timestamp} />
              </div>
            )}
            {conteudo}
            {resto}
          </div>
        </div>
      )}
      {pode && !editando && <Acoes mensagem={mensagem} pode={pode} />}
    </article>
  );
});

/** A thread que saiu da mensagem: nome, quantas mensagens; abre ao lado. */
function ChipThread({ mensagem }: { mensagem: APIMessage }) {
  const navegar = useNavigate();
  const canal = useCanal();
  const reserva = mensagem.thread!;
  const thread = useEstado((estado) => estado.channels[reserva.id]) ?? reserva;
  const total = thread.message_count ?? 0;
  return (
    <button
      type="button"
      onClick={() =>
        navegar(
          `/canais/${canal.guildId ?? '@me'}/${canal.canalId}/threads/${thread.id}`,
        )
      }
      className="mt-1 flex w-fit max-w-full items-center gap-2 rounded-lg border border-borda bg-elevado px-3 py-2 text-left text-sm hover:border-texto-3"
    >
      <Icone nome="thread" tamanho={16} className="shrink-0 text-texto-3" />
      <span className="truncate font-semibold">{thread.name}</span>
      <span className="shrink-0 font-medium text-link">
        {total} {total === 1 ? 'mensagem' : 'mensagens'}
      </span>
      {thread.thread_metadata?.archived && (
        <span className="shrink-0 text-xs text-texto-3">Arquivada</span>
      )}
      <Icone
        nome="chevron-direita"
        tamanho={14}
        className="shrink-0 text-texto-3"
      />
    </button>
  );
}

/** Mensagem saindo: enviando (apagada), na fila, ou recusada (vermelha). */
export function MensagemPendente({
  pendente,
  agrupada,
}: {
  pendente: PendingMessage;
  agrupada: boolean;
}) {
  const cliente = useCliente();
  const canal = useCanal();
  const eu = useEu();
  const quando = new Date(pendente.createdAt).toISOString();
  const falhou = pendente.status === 'failed';
  return (
    <article
      aria-label={falhou ? 'Mensagem não enviada' : 'Mensagem enviando'}
      className={`group relative flex gap-4 px-4 ${agrupada ? 'py-0.5' : 'pt-[14px] pb-0.5'}`}
    >
      {agrupada ? (
        <span className="w-10 shrink-0" />
      ) : (
        <Avatar pessoa={eu} nome={eu.global_name ?? eu.username} />
      )}
      <div className="flex min-w-0 grow flex-col gap-0.5">
        {!agrupada && (
          <div className="flex items-baseline gap-2">
            <NomeDoAutor autor={eu} guildId={canal.guildId} />
            <Hora quando={quando} />
          </div>
        )}
        {pendente.content && (
          <div
            className={`text-[15px] leading-[1.45] ${falhou ? 'text-ocupado' : 'opacity-50'}`}
          >
            <Markdown texto={pendente.content} guildId={canal.guildId} />
            <span className="sr-only">
              {falhou ? ' (não enviada)' : ' (enviando)'}
            </span>
          </div>
        )}
        {pendente.attachments?.map((anexo) => (
          <span
            key={anexo.name}
            className={`flex items-center gap-1.5 text-[13px] text-texto-3 ${falhou ? '' : 'opacity-60'}`}
          >
            <Icone nome="clipe" tamanho={14} />
            {anexo.name}
          </span>
        ))}
        {pendente.status === 'queued' && (
          <span className="flex items-center gap-1.5 text-[13px] text-texto-3">
            <Icone nome="relogio" tamanho={14} />
            {t('mensagem.naFila')}
            <span aria-hidden="true" className="text-apagado">
              ·
            </span>
            <button
              type="button"
              onClick={() =>
                void cliente.discardPending(pendente.channelId, pendente.nonce)
              }
              className="font-medium text-link hover:underline"
            >
              Apagar
            </button>
          </span>
        )}
        {falhou && (
          <span className="flex flex-wrap items-center gap-1.5 text-[13px] text-ocupado">
            <Icone nome="alerta" tamanho={14} />
            <span>
              Não foi possível enviar
              {pendente.error ? `: ${pendente.error}` : '.'}
            </span>
            <button
              type="button"
              onClick={() =>
                cliente
                  .retryPending(pendente.channelId, pendente.nonce)
                  .catch(avisarErro)
              }
              className="font-medium text-link hover:underline"
            >
              Tentar de novo
            </button>
            <span aria-hidden="true" className="text-apagado">
              ·
            </span>
            <button
              type="button"
              onClick={() =>
                void cliente.discardPending(pendente.channelId, pendente.nonce)
              }
              className="font-medium text-link hover:underline"
            >
              Apagar
            </button>
          </span>
        )}
      </div>
    </article>
  );
}

export function DivisoriaDia({ data }: { data: string }) {
  return (
    <div role="separator" className="mx-4 mt-4 mb-1 flex items-center gap-2">
      <span className="h-px grow bg-borda" />
      <span className="text-xs font-semibold text-texto-3">
        {formatDayDivider(data)}
      </span>
      <span className="h-px grow bg-borda" />
    </div>
  );
}

export function DivisoriaNovas() {
  return (
    <div
      role="separator"
      aria-label="Novas mensagens"
      className="mx-4 mt-3.5 mb-1 flex items-center"
    >
      <span className="h-px grow bg-destaque" />
      <span className="rounded bg-destaque px-1.5 py-px text-[11px] font-bold tracking-[.04em] text-destaque-texto uppercase">
        {t('mensagem.novas')}
      </span>
    </div>
  );
}

/** Esqueleto enquanto as mensagens chegam (C51). */
export function Esqueleto({ linhas = 3 }: { linhas?: number }) {
  const larguras = ['72%', '48%', '86%', '35%', '64%'];
  return (
    <div aria-hidden="true" className="flex flex-col">
      {Array.from({ length: linhas }, (_, indice) => (
        <div key={indice} className="flex gap-4 px-4 pt-2.5 pb-2.5">
          <span className="size-10 shrink-0 rounded-full bg-elevado" />
          <div className="flex grow flex-col gap-2 pt-1">
            <span className="h-3 w-[120px] rounded bg-elevado" />
            <span
              className="h-3 rounded bg-elevado"
              style={{ width: larguras[indice % larguras.length] }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
